import React, { useEffect, useRef, useState } from "react";
import "./resellerStudio.css";
import { searchParties, type PartyHit } from "./ShareRecipientNameField";
import { isPlausibleSharePhone, partyPhoneToShareFields } from "./catalogueShareCountries";

export type MatchedParty = { name: string; email: string; phone: string; countryIso: string };

type Props = {
  phone: string;
  countryIso: string;
  /** Name typed in the form. */
  name: string;
  /** Fill the form from the saved party. */
  onUseParty: (party: MatchedParty) => void;
};

type State =
  | { kind: "idle" }
  | { kind: "checking" }
  | { kind: "found"; party: PartyHit }
  | { kind: "new"; nameClash?: PartyHit }
  | { kind: "error" };

/** Last 10 digits: parties are stored as 10-digit or 91-prefixed numbers. */
export const phoneKey = (raw: string) => raw.replace(/\D/g, "").slice(-10);

const sameName = (a: string, b: string) => a.trim().toLowerCase().replace(/\s+/g, " ") === b.trim().toLowerCase().replace(/\s+/g, " ");

/**
 * Under the mobile field of a share form: is this number already a party? If so its details fill
 * in; if not, the party is created when the link is saved (the backend does this). Also warns when
 * the typed name belongs to a party with a different number.
 */
const SharePartyMatch: React.FC<Props> = ({ phone, countryIso, name, onUseParty }) => {
  const [state, setState] = useState<State>({ kind: "idle" });
  const autoFilled = useRef("");
  const seq = useRef(0);

  useEffect(() => {
    const key = phoneKey(phone);
    if (!isPlausibleSharePhone(phone, countryIso) || key.length < 8) {
      setState({ kind: "idle" });
      return;
    }
    const mine = ++seq.current;
    setState({ kind: "checking" });
    const t = window.setTimeout(async () => {
      try {
        const hits = await searchParties(key);
        if (mine !== seq.current) return;
        const match = hits.find((h) => phoneKey(h.phone) === key);
        if (match) {
          setState({ kind: "found", party: match });
          // Fill an empty form once per number; never overwrite what the user typed.
          if (!name.trim() && autoFilled.current !== key) {
            autoFilled.current = key;
            onUseParty({ ...partyFields(match), name: match.name });
          }
          return;
        }
        let nameClash: PartyHit | undefined;
        if (name.trim().length >= 2) {
          const byName = await searchParties(name.trim());
          if (mine !== seq.current) return;
          nameClash = byName.find((h) => sameName(h.name, name) && h.phone && phoneKey(h.phone) !== key);
        }
        setState({ kind: "new", nameClash });
      } catch {
        if (mine === seq.current) setState({ kind: "error" });
      }
    }, 400);
    return () => window.clearTimeout(t);
    // Re-check when the number or name changes; onUseParty is a fresh closure each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phone, countryIso, name]);

  if (state.kind === "idle") return null;
  if (state.kind === "checking") {
    return <p className="hkp-party-match" role="status">Checking Parties…</p>;
  }
  if (state.kind === "error") {
    return (
      <p className="hkp-party-match" role="status">
        Couldn't check Parties. A new party is still saved with the link if this number is new.
      </p>
    );
  }
  if (state.kind === "found") {
    const p = state.party;
    const differs = name.trim() && !sameName(name, p.name);
    return (
      <p className="hkp-party-match is-found" role="status">
        <span>✓ Saved party: {p.name}</span>
        {differs ? (
          <button type="button" onClick={() => onUseParty({ ...partyFields(p), name: p.name })}>
            Use “{p.name}”
          </button>
        ) : null}
      </p>
    );
  }
  if (state.nameClash) {
    return (
      <p className="hkp-party-match is-warn" role="alert">
        <span>
          {state.nameClash.name} is already a party with a number ending {phoneKey(state.nameClash.phone).slice(-4)}.
          Check the mobile, or use their saved number.
        </span>
        <button type="button" onClick={() => onUseParty({ ...partyFields(state.nameClash!), name: state.nameClash!.name })}>
          Use saved number
        </button>
      </p>
    );
  }
  return (
    <p className="hkp-party-match" role="status">
      New number: {name.trim() || "this person"} will be added to Parties as a customer when you create the link.
    </p>
  );
};

function partyFields(p: PartyHit): Omit<MatchedParty, "name"> {
  const fields = partyPhoneToShareFields(p.phone);
  return { email: p.email || "", phone: fields.phone, countryIso: fields.countryIso };
}

export default SharePartyMatch;
